import React, { useState } from 'react';
import { Button, Paper, Stack, Typography } from '@mui/material';
import { useAccounts } from '../../hooks/useAccounts';
import { useAuth } from '../../context/AuthContext';
import ManageAssetAccountsModal from './ManageAssetAccountsModal';

const AssetAccountsWidget: React.FC = () => {
  const { auth } = useAuth();
  const { checkingAccounts, savingsAccounts, loading, error, refetch } = useAccounts();
  const [manageCheckingOpen, setManageCheckingOpen] = useState(false);
  const [manageSavingsOpen, setManageSavingsOpen] = useState(false);

  const visibleCheckingAccounts = checkingAccounts.filter((account) => !account.isArchived);
  const visibleSavingsAccounts = savingsAccounts.filter((account) => !account.isArchived);
  const hasNoBankAccounts = visibleCheckingAccounts.length === 0 && visibleSavingsAccounts.length === 0;

  return (
    <Paper sx={{ p: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
        <Typography variant="h6" fontWeight="bold">
          Bank Accounts
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Create and manage your checking and savings accounts.
      </Typography>

      {loading ? (
        <Typography color="text.secondary">Loading accounts...</Typography>
      ) : error ? (
        <Typography color="error.main">{error}</Typography>
      ) : (
        <Stack spacing={1.5}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1}>
            <Typography fontWeight={600}>Checking</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button size="small" href="/accounts/checking">
                View all
              </Button>
              <Button size="small" onClick={() => setManageCheckingOpen(true)}>
                Manage Checking
              </Button>
            </Stack>
          </Stack>
          {visibleCheckingAccounts.length === 0 ? (
            <Typography color="text.secondary">No checking accounts found.</Typography>
          ) : (
            visibleCheckingAccounts.map((account) => (
              <Typography key={account._id} variant="body2">
                {account.name}
              </Typography>
            ))
          )}

          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1}>
            <Typography fontWeight={600}>Savings</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button size="small" href="/accounts/savings">
                View all
              </Button>
              <Button size="small" onClick={() => setManageSavingsOpen(true)}>
                Manage Savings
              </Button>
            </Stack>
          </Stack>
          {visibleSavingsAccounts.length === 0 ? (
            <Typography color="text.secondary">No savings accounts found.</Typography>
          ) : (
            visibleSavingsAccounts.map((account) => (
              <Typography key={account._id} variant="body2">
                {account.name}
              </Typography>
            ))
          )}

          {hasNoBankAccounts && (
            <>
              <Typography color="text.secondary">
                No checking or savings accounts yet. Create both to start adding transactions.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <Button variant="outlined" onClick={() => setManageCheckingOpen(true)}>
                  Create Checking
                </Button>
                <Button variant="outlined" onClick={() => setManageSavingsOpen(true)}>
                  Create Savings
                </Button>
              </Stack>
            </>
          )}
        </Stack>
      )}

      <ManageAssetAccountsModal
        open={manageCheckingOpen}
        onClose={() => setManageCheckingOpen(false)}
        accountType="checking"
        accounts={visibleCheckingAccounts}
        token={auth.token}
        onSaved={refetch}
      />
      <ManageAssetAccountsModal
        open={manageSavingsOpen}
        onClose={() => setManageSavingsOpen(false)}
        accountType="savings"
        accounts={visibleSavingsAccounts}
        token={auth.token}
        onSaved={refetch}
      />
    </Paper>
  );
};

export default AssetAccountsWidget;
